import axios, { AxiosResponse } from "axios"
import { UUIDType } from "uuid"
import { InsertCuentaBancariaCommand } from "./Commands/InsertCuentaBancariaCommand"
import { UpdateCuentaBancariaCommand } from "./Commands/UpdateCuentaBancariaCommand"
import {
  cuentaBancariaByFiltersApadater,
  cuentaBancariaByIdApadater,
  movimientosPorConciliarAdapter,
  movimientosPorConciliarAutomaticamenteAdapter,
} from "./CuentaBancaria.adapter"
import { GetCuentasBancariasByFiltersDtoProps } from "./DTOs/GetCuentasBancariasByFiltersDto"
import { GetCuentasBancariasByIdQueryDtoProps } from "./DTOs/GetCuentasBancariasByIdQueryDto"
import { GetMovimientosPorConciliarAutomaticamenteDtoProps } from "./DTOs/GetMovimientosPorConciliarAutomaticamenteDto"
import { GetSaldosCuentaBancariaDtoProps } from "./DTOs/GetSaldosCuentaBancariaDto"
import { MovimientoBancarioReadingDtoProps } from "./DTOs/MovimientoBancarioReadingDto"
import { GetCuentasBancariasByFiltersDtoIPagedEnumerableProps } from "./Schemas/GetCuentasBancariasByFiltersDtoIPagedEnumerable"
import { GetMovimientosPorConciliarAutomaticamenteDtoIPagedEnumerableProps } from "./Schemas/GetMovimientosPorConciliarAutomaticamenteDtoIPagedEnumerable"
import { MovimientoBancarioReadingDtoIPagedEnumerableProps } from "./Schemas/MovimientoBancarioReadingDtoIPagedEnumerable"
import { EstadoConciliacionType } from "./Types/EstadoConciliacion"
import { TipoTransaccionType } from "./Types/TipoTransaccionEnum"
import { SortingCriteria } from "./interfaces/SortingCriteria"

export interface MovimientosPorConciliarAutomáticamenteQueryParamsProps {
  id?: UUIDType | string
  AmountLowerLimit?: number
  AmountUpperLimit?: number
  BookingDateLowerLimit?: string
  BookingDateUpperLimit?: string
  SortingCriteria?: SortingCriteria
  MaxResultCount?: number
  SkipCount?: number
}

export interface IbanMovimientosParamsProps {
  Iban: string
  EsConciliacionForzada?: boolean
  EstadosConciliaciones?: EstadoConciliacionType
  AmountLowerLimit?: number
  AmountUpperLimit?: number
  BookingDateLowerLimit?: string
  BookingDateUpperLimit?: string
  Keyword?: string
  Type?: TipoTransaccionType
  SortingCriteria?: SortingCriteria
  IsEnabled?: boolean
  MaxResultCount?: number
  SkipCount?: number
}

export async function getMovimientosPorConciliarAutomaticamente(
  queryParams: MovimientosPorConciliarAutomáticamenteQueryParamsProps
): Promise<GetMovimientosPorConciliarAutomaticamenteDtoIPagedEnumerableProps> {
  const { id, ...restParams } = queryParams
  const { data } = await axios.get(
    `/CuentaBancaria/${id}/movimientos-por-conciliar-automaticamente`,
    {
      params: restParams,
    }
  )
  return {
    currentPage: data?.currentPage,
    items: data?.items?.map(
      (movimiento: GetMovimientosPorConciliarAutomaticamenteDtoProps) =>
        movimientosPorConciliarAutomaticamenteAdapter(movimiento)
    ),
    totalCount: data?.totalCount,
  }
}

export async function getCuentaBancariaById(
  id: string
): Promise<GetCuentasBancariasByIdQueryDtoProps> {
  const { data } = await axios.get(`/CuentaBancaria/${id}`)
  return cuentaBancariaByIdApadater(data)
}

export async function getAllCuentasBancarias(): Promise<GetCuentasBancariasByFiltersDtoIPagedEnumerableProps> {
  const { data } = await axios.get("/CuentaBancaria")
  return {
    currentPage: data?.currentPage,
    items: data?.items?.map(
      (cuentaBancaria: GetCuentasBancariasByFiltersDtoProps) =>
        cuentaBancariaByFiltersApadater(cuentaBancaria)
    ),
    totalCount: data?.totalCount,
  }
}

export async function putCuentaBancariaById({
  cuentaBancaria,
  id,
}: {
  cuentaBancaria: UpdateCuentaBancariaCommand
  id: string
}) {
  return axios.put(`/cuentaBancaria/${id}`, cuentaBancaria)
}

export async function postCuentaBancaria(
  cuentaBancaria: InsertCuentaBancariaCommand
) {
  return axios.post(`/cuentaBancaria`, cuentaBancaria)
}

export async function getMovimientosByCuentaBancaria(
  queryParams: IbanMovimientosParamsProps,
  iban: string
): Promise<MovimientoBancarioReadingDtoIPagedEnumerableProps> {
  const { data } = await axios.get(`/cuentaBancaria/${iban}/movimientos`, {
    params: queryParams,
  })
  return {
    currentPage: data?.currentPage,
    items: data?.items?.map((movimiento: MovimientoBancarioReadingDtoProps) =>
      movimientosPorConciliarAdapter(movimiento)
    ),
    totalCount: data?.totalCount,
  }
}

interface MovBancariosExportarExcelQueryParamsProps {
  FechaLowerLimit: string
  FechaUpperLimit: string
}

export async function getMovimientosBancariosExportarExcel(
  id: string,
  queryParams: MovBancariosExportarExcelQueryParamsProps
) {
  return axios.get(`/cuentaBancaria/${id}/movimientos/exportar-excel`, {
    params: queryParams,
    responseType: "blob",
  })
}

export async function deleteCuentaBancaria(id: string): Promise<AxiosResponse> {
  const response = await axios.delete(`/cuentaBancaria/${id}`)
  return response
}

export async function getSaldosCuentaBancaria(
  id: string,
  queryParams: {
    FechaUpperLimit: string | Date
  }
): Promise<GetSaldosCuentaBancariaDtoProps> {
  const { data } = await axios.get(`/cuentaBancaria/${id}/saldos`, {
    params: queryParams,
  })
  return data
}

export async function postBankRevokeAuthorization(
  id: string
): Promise<AxiosResponse> {
  const response = await axios.post(`/cuentaBancaria/${id}/revocar`)
  return response
}

export async function getSyncCuentaBancariaById(
  id: string
): Promise<AxiosResponse> {
  const resp = await axios.post(`/cuentaBancaria/${id}/sincronizar`)
  return resp
}
